import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Anchor, Info } from "lucide-react";
import { IMG } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVELS = [
  "Sem curso (Batismo)",
  "Open Water",
  "Advanced",
  "Rescue Diver",
  "Divemaster",
  "Instrutor",
];

const INITIAL = {
  nome_completo: "",
  email: "",
  cpf: "",
  telefone: "",
  profissao: "",
  data_nascimento: "",
  altura_cm: "",
  peso_kg: "",
  numero_calcado: "",
  data_expedicao_inicio: "",
  data_expedicao_fim: "",
  nivel_atual: "",
  fara_curso: false,
  qual_curso: "",
  equipamento_roupa: false,
  equipamento_colete: false,
  equipamento_regulador: false,
  alugar_lanterna: false,
  alugar_computador: false,
  data_voo_ida: "",
  horario_voo_ida: "",
  data_voo_volta: "",
  horario_voo_volta: "",
  cidade_voo: "",
  chegada_caravelas: "",
  contato_emergencia: "",
  restricao_alimentar: "",
  alergia_medicacao: "",
  observacoes: "",
};

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-bold text-white">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-white/60">{hint}</span>}
    </label>
  );
}

const inputCls =
  "w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-base text-white placeholder-white/40 outline-none backdrop-blur-md transition-colors focus:border-sun focus:bg-white/15";

export default function DadosAbrolhos() {
  const [f, setF] = useState(INITIAL);
  const [status, setStatus] = useState("idle");

  React.useEffect(() => {
    document.title = "Expedição Abrolhos — Cadastro | Dive Master";
  }, []);

  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (status === "loading") return;
    setStatus("loading");
    try {
      const payload = {
        ...f,
        altura_cm: parseFloat(f.altura_cm),
        peso_kg: parseFloat(f.peso_kg),
        numero_calcado: parseInt(f.numero_calcado, 10),
        qual_curso: f.fara_curso ? f.qual_curso : "",
        equipamentos_detalhes: f.precisa_equipamento ? f.equipamentos_detalhes : "",
      };
      await axios.post(`${API}/abrolhos`, payload);
      setStatus("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setStatus("idle");
      alert("Não foi possível enviar. Verifique os dados e tente novamente.");
    }
  };

  if (status === "success") {
    return (
      <main
        className="relative flex min-h-[100svh] items-center justify-center overflow-hidden light-rays"
        style={{ background: "linear-gradient(180deg,#0F3A86,#05123A)" }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-ocean-deep/40 via-transparent to-ocean-deep/80" />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 mx-auto max-w-xl px-6 py-16 text-center"
          data-testid="abrolhos-success"
        >
          <img src={IMG.logo} alt="Dive Master" className="mx-auto h-20 w-auto sm:h-24" />
          <div className="mx-auto mt-8 flex h-20 w-20 items-center justify-center rounded-full bg-sun shadow-2xl shadow-sun/40">
            <CheckCircle2 className="h-11 w-11 text-white" strokeWidth={2.4} />
          </div>
          <h1 className="mt-8 font-display text-4xl font-black uppercase leading-tight tracking-tight text-white sm:text-5xl">
            Dados recebidos!
          </h1>
          <p className="mt-4 text-base text-white/85 sm:text-lg">
            Nossa equipe recebeu suas informações da <strong>Expedição Abrolhos</strong> e
            entrará em contato pelo WhatsApp em breve com os próximos passos.
          </p>
        </motion.div>
      </main>
    );
  }

  return (
    <main
      className="relative min-h-[100svh] overflow-hidden light-rays"
      style={{ background: "linear-gradient(180deg,#0F3A86,#05123A)" }}
      data-testid="abrolhos-form-page"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-ocean-deep/40 via-transparent to-ocean-deep/80" />

      <div className="relative z-10 mx-auto max-w-2xl px-5 py-10 sm:py-14">
        {/* Header */}
        <div className="flex items-center justify-between">
          <img src={IMG.logo} alt="Dive Master" className="h-12 w-auto drop-shadow-lg sm:h-14" />
          <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-white backdrop-blur">
            Expedição Abrolhos
          </span>
        </div>

        <div className="mt-8">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-ocean-glow">
            Cadastro de participante
          </p>
          <h1 className="mt-3 font-display text-3xl font-black uppercase leading-tight tracking-tight text-white sm:text-4xl">
            Preencha seus dados da expedição
          </h1>
          <p className="mt-3 text-sm text-white/75 sm:text-base">
            Precisamos dessas informações para preparar seu equipamento, agendar mergulhos
            e organizar sua chegada em Caravelas.
          </p>
        </div>

        <form onSubmit={submit} className="mt-10 space-y-6" data-testid="abrolhos-form">
          {/* Personal */}
          <fieldset className="space-y-4 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
            <legend className="px-2 text-[11px] font-black uppercase tracking-[0.2em] text-sun">
              Dados pessoais
            </legend>

            <Field label="Nome completo *">
              <input
                required
                type="text"
                className={inputCls}
                value={f.nome_completo}
                onChange={(e) => set("nome_completo", e.target.value)}
                data-testid="ab-nome"
              />
            </Field>

            <Field label="E-mail *">
              <input
                required
                type="email"
                className={inputCls}
                value={f.email}
                onChange={(e) => set("email", e.target.value)}
                data-testid="ab-email"
              />
            </Field>

               <Field label="CPF *">
              <input
                required
                type="text"
                placeholder="000.000.000-00"
                className={inputCls}
                value={f.cpf}
                onChange={(e) => set("cpf", e.target.value)}
                data-testid="ab-cpf"
              />
            </Field>

            <Field label="Telefone *">
              <input
                required
                type="tel"
                placeholder="(47) 99999-9999"
                className={inputCls}
                value={f.telefone}
                onChange={(e) => set("telefone", e.target.value)}
                data-testid="ab-telefone"
              />
            </Field>

            <Field label="Profissão *">
              <input
                required
                type="text"
                className={inputCls}
                value={f.profissao}
                onChange={(e) => set("profissao", e.target.value)}
                data-testid="ab-profissao"
              />
            </Field>

            <Field label="Data de nascimento *">
              <input
                required
                type="date"
                className={inputCls}
                value={f.data_nascimento}
                onChange={(e) => set("data_nascimento", e.target.value)}
                data-testid="ab-nasc"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Altura (cm) *">
                <input
                  required
                  type="number"
                  min="50"
                  max="250"
                  step="1"
                  placeholder="170"
                  className={inputCls}
                  value={f.altura_cm}
                  onChange={(e) => set("altura_cm", e.target.value)}
                  data-testid="ab-altura"
                />
              </Field>
              <Field label="Peso (kg) *">
                <input
                  required
                  type="number"
                  min="20"
                  max="250"
                  step="0.1"
                  placeholder="70"
                  className={inputCls}
                  value={f.peso_kg}
                  onChange={(e) => set("peso_kg", e.target.value)}
                  data-testid="ab-peso"
                />
              </Field>
            </div>

            <Field label="Número do calçado *">
              <input
                required
                type="number"
                min="20"
                max="60"
                className={inputCls}
                value={f.numero_calcado}
                onChange={(e) => set("numero_calcado", e.target.value)}
                data-testid="ab-calcado"
              />
            </Field>
          </fieldset>

          {/* Expedition */}
          <fieldset className="space-y-4 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
            <legend className="px-2 text-[11px] font-black uppercase tracking-[0.2em] text-sun">
              Expedição
            </legend>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Data inicial da expedição *">
                <input
                  required
                  type="date"
                  className={inputCls}
                  value={f.data_expedicao_inicio}
                  onChange={(e) => set("data_expedicao_inicio", e.target.value)}
                  data-testid="ab-data-exp-inicio"
                />
              </Field>
              <Field label="Data final da expedição *">
                <input
                  required
                  type="date"
                  className={inputCls}
                  value={f.data_expedicao_fim}
                  onChange={(e) => set("data_expedicao_fim", e.target.value)}
                  data-testid="ab-data-exp-fim"
                />
              </Field>
            </div>

            <Field label="Nível de curso de mergulho atual *">
              <select
                required
                className={inputCls}
                value={f.nivel_atual}
                onChange={(e) => set("nivel_atual", e.target.value)}
                data-testid="ab-nivel"
              >
                <option value="" disabled className="text-slate-500">
                  Selecione seu nível
                </option>
                {LEVELS.map((l) => (
                  <option key={l} value={l} className="bg-brand-navy text-white">
                    {l}
                  </option>
                ))}
              </select>
            </Field>

            <div className="rounded-xl bg-sun/10 p-3">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={f.fara_curso}
                  onChange={(e) => set("fara_curso", e.target.checked)}
                  className="mt-1 h-5 w-5 accent-sun"
                  data-testid="ab-fara-curso"
                />
                <span className="text-sm font-bold text-white">
                  Vou fazer algum curso durante a expedição
                </span>
              </label>
              {f.fara_curso && (
                <div className="mt-3">
                  <input
                    type="text"
                    placeholder="Qual curso? (ex: Open Water, Advanced, Nitrox)"
                    className={inputCls}
                    value={f.qual_curso}
                    onChange={(e) => set("qual_curso", e.target.value)}
                    data-testid="ab-qual-curso"
                  />
                </div>
              )}
              <p className="mt-3 flex items-start gap-2 text-xs text-white/70">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ocean-glow" />
                Para os mergulhos noturnos é preciso ter o curso Advanced ou fazê-lo durante a expedição.
              </p>
            </div>
          </fieldset>

          {/* Equipment */}
          <fieldset className="space-y-4 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
            <legend className="px-2 text-[11px] font-black uppercase tracking-[0.2em] text-sun">
              Equipamentos
            </legend>

            <div>
              <p className="mb-2 text-sm font-bold text-white">
                Quais equipamentos você precisa?
              </p>
              <p className="text-xs text-white/60 mb-3">
                Marque as opções apenas se você for mergulhador certificado, para quem vai como acompanhante e pretende fazer batismo, os equipamentos estão inclusos no valor do batismo.
              </p>
              <div className="space-y-2 ">
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    checked={f.equipamento_roupa}
                    onChange={(e) => set("equipamento_roupa", e.target.checked)}
                    className="h-5 w-5 accent-sun"
                    data-testid="ab-eq-roupa"
                  />
                  <span className="text-sm text-white">
                    Roupa — <strong className="font-bold text-white">R$ 60/dia</strong>
                  </span>
                </label>
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    checked={f.equipamento_colete}
                    onChange={(e) => set("equipamento_colete", e.target.checked)}
                    className="h-5 w-5 accent-sun"
                    data-testid="ab-eq-colete"
                  />
                  <span className="text-sm text-white">
                    Colete — <strong className="font-bold text-white">R$ 60/dia</strong>
                  </span>
                </label>
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    checked={f.equipamento_regulador}
                    onChange={(e) => set("equipamento_regulador", e.target.checked)}
                    className="h-5 w-5 accent-sun"
                    data-testid="ab-eq-regulador"
                  />
                  <span className="text-sm text-white">
                    Regulador — <strong className="font-bold text-white">R$ 60/dia</strong>
                  </span>
                </label>
              </div>
            </div>

            <div className="rounded-xl border-2 border-sun/40 bg-sun/10 p-4">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={f.alugar_lanterna}
                  onChange={(e) => set("alugar_lanterna", e.target.checked)}
                  className="mt-1 h-5 w-5 accent-sun"
                  data-testid="ab-alugar-lanterna"
                />
                <span className="text-sm text-white">
                  <strong className="font-bold text-sun">Quero alugar a lanterna</strong> por{" "}
                  <strong className="font-bold text-white">R$ 80/dia</strong>
                </span>
              </label>
              <p className="mt-3 flex items-start gap-2 text-xs text-white/70">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ocean-glow" />
                A lanterna é obrigatória para os mergulhos noturnos.
              </p>
            </div>

            <div className="rounded-xl border border-white/15 bg-white/5 p-4">
              <label className="flex cursor-pointer items-start gap-3">
                <input
                  type="checkbox"
                  checked={f.alugar_computador}
                  onChange={(e) => set("alugar_computador", e.target.checked)}
                  className="mt-1 h-5 w-5 accent-sun"
                  data-testid="ab-alugar-computador"
                />
                <span className="text-sm text-white">
                  <strong className="font-bold text-white">Quero alugar o computador de mergulho</strong> por{" "}
                  <strong className="font-bold text-white">R$ 120/dia</strong>
                </span>
              </label>
              <p className="mt-3 flex items-start gap-2 text-xs text-white/70">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ocean-glow" />
                O computador de mergulho não é obrigatório.
              </p>
            </div>

            <p className="text-xs text-white/60">
              Nadadeira, máscara, snorkel, cilindro e lastro estão inclusos.
            </p>
          </fieldset>

          {/* Travel */}
          <fieldset className="space-y-4 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
            <legend className="px-2 text-[11px] font-black uppercase tracking-[0.2em] text-sun">
              Viagem e chegada
            </legend>

            {/* Ida */}
            <div className="space-y-3">
              <p className="text-xs font-bold uppercase tracking-wider text-white/60">
                Voo de ida
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Data do voo (ida)">
                  <input
                    type="date"
                    className={inputCls}
                    value={f.data_voo_ida}
                    onChange={(e) => set("data_voo_ida", e.target.value)}
                    data-testid="ab-data-voo-ida"
                  />
                </Field>
                <Field label="Horário de chegada (ida)">
                  <input
                    type="time"
                    className={inputCls}
                    value={f.horario_voo_ida}
                    onChange={(e) => set("horario_voo_ida", e.target.value)}
                    data-testid="ab-hora-voo-ida"
                  />
                </Field>
              </div>
            </div>

            <Field label="Cidade de chegada do voo">
              <input
                type="text"
                placeholder="ex: Porto Seguro, Salvador, Teixeira de Freitas..."
                className={inputCls}
                value={f.cidade_voo}
                onChange={(e) => set("cidade_voo", e.target.value)}
                data-testid="ab-cidade-voo"
              />
            </Field>

            <Field
              label="Como você vai chegar em Caravelas?"
              hint="Vai sozinho(a), dividir carro com alguém, transfer, ônibus... conte os detalhes"
            >
              <textarea
                rows={4}
                placeholder="ex: Vou de carro com mais 2 amigos saindo de Porto Seguro no dia..."
                className={inputCls}
                value={f.chegada_caravelas}
                onChange={(e) => set("chegada_caravelas", e.target.value)}
                data-testid="ab-chegada"
              />
            </Field>

                        {/* Volta */}
            <div className="space-y-3">
              <p className="text-xs font-bold uppercase tracking-wider text-white/60">
                Voo de volta
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Data do voo (volta)">
                  <input
                    type="date"
                    className={inputCls}
                    value={f.data_voo_volta}
                    onChange={(e) => set("data_voo_volta", e.target.value)}
                    data-testid="ab-data-voo-volta"
                  />
                </Field>
                <Field label="Horário de partida (volta)">
                  <input
                    type="time"
                    className={inputCls}
                    value={f.horario_voo_volta}
                    onChange={(e) => set("horario_voo_volta", e.target.value)}
                    data-testid="ab-hora-voo-volta"
                  />
                </Field>
              </div>
            </div>
          </fieldset>

          {/* Saúde e emergência */}
          <fieldset className="space-y-4 rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm">
            <legend className="px-2 text-[11px] font-black uppercase tracking-[0.2em] text-sun">
              Saúde e emergência
            </legend>

            <Field
              label="Contato de emergência *"
              hint="Nome, parentesco e telefone (com DDD) de alguém para contatarmos se necessário"
            >
              <input
                required
                type="text"
                placeholder="ex: Maria Silva (mãe) - (47) 99999-9999"
                className={inputCls}
                value={f.contato_emergencia}
                onChange={(e) => set("contato_emergencia", e.target.value)}
                data-testid="ab-contato-emergencia"
              />
            </Field>

            <Field
              label="Restrição alimentar"
              hint="Ex: vegetariano, vegano, sem glúten, sem lactose, alergia a frutos do mar... Deixe em branco se nenhuma."
            >
              <textarea
                rows={2}
                placeholder="Ex: Vegetariano / Sem lactose / Nenhuma"
                className={inputCls}
                value={f.restricao_alimentar}
                onChange={(e) => set("restricao_alimentar", e.target.value)}
                data-testid="ab-restricao"
              />
            </Field>

            <Field
              label="Alergia a alguma medicação?"
              hint="Liste medicações que você não pode tomar (ou 'nenhuma')"
            >
              <textarea
                rows={2}
                placeholder="Ex: Dipirona, Penicilina / Nenhuma"
                className={inputCls}
                value={f.alergia_medicacao}
                onChange={(e) => set("alergia_medicacao", e.target.value)}
                data-testid="ab-alergia"
              />
            </Field>

            <Field label="Observações adicionais (opcional)">
              <textarea
                rows={3}
                placeholder="Qualquer outra informação importante para a organização"
                className={inputCls}
                value={f.observacoes}
                onChange={(e) => set("observacoes", e.target.value)}
                data-testid="ab-obs"
              />
            </Field>
          </fieldset>

          <button
            type="submit"
            disabled={status === "loading"}
            className="cta-sun inline-flex w-full items-center justify-center gap-2 rounded-full px-8 py-5 text-base font-extrabold uppercase tracking-wide text-white shadow-2xl shadow-sun/40 disabled:opacity-70 sm:text-lg"
            data-testid="ab-submit"
          >
            {status === "loading" ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" /> Enviando...
              </>
            ) : (
              <>
                <Anchor className="h-5 w-5" /> Enviar dados da expedição
              </>
            )}
          </button>
        </form>
      </div>
    </main>
  );
}
